/**
 * Detects prescription values that the product being ordered does not offer.
 *
 * WHY THIS EXISTS
 * ---------------
 * Reusing a past prescription copies whole regions of deep-state verbatim,
 * across materials and across product families. That is a deliberate, wanted
 * capability. The hazard is what happens when a copied value is not on the
 * target's option list: the select renders BLANK (a <select> whose value
 * matches no <option> has selectedIndex -1) while the illegal value stays live
 * in state, its dependent fields keep rendering because they are gated on the
 * raw value, and the Excel exporter prints it. The operator sees an empty box;
 * the lab receives a spec. That asymmetry is the bug.
 *
 * WHY IT IS A PURE FUNCTION AND NOT COMPONENT-REPORTED
 * ---------------------------------------------------
 * The obvious design is to let each section report its own violations as it
 * renders, since each already computes its own option list. It does not work:
 * `CollapsibleCard` UNMOUNTS its children when collapsed, and every
 * prescription section is collapsed by default. The whole point of copying a
 * prescription is not having to open the sections, so in exactly the case this
 * check exists for, a render-driven collector reports nothing and the gate is
 * vacuously green. `prescriptionFormDefaults.ts` documents a shipped production
 * bug of precisely this shape.
 *
 * So legality is computed over form state, by functions that also feed the
 * rendered option lists, so the two cannot drift.
 *
 * WHAT THIS IS NOT
 * ----------------
 * This is the LOOKUP half ("does the target offer this value"). It is not the
 * MAPPING half ("what should the value become instead"), which is the shared
 * legality module, housekeeping backlog item 5. Nothing here substitutes a
 * value except the single-option case, where there is no clinical judgement to
 * make. Everything else is handed to the podiatrist to decide.
 */

import type { PrescriptionFormState } from './prescriptionFormDefaults';
import type { SolutionSubtype } from './SolutionSubtypePicker';
import { CARD_IDS } from './prescription/cardIds';
import {
  normalizeRearfootWedgeType,
  rearfootWedgeTypesFor,
} from './prescription/rearfootWedgeRules';
import {
  heelRaiseLengthsFor,
  heelRaiseTypesFor,
  subcalOffloadingTypesFor,
} from './prescription/heelRaiseRules';
import {
  forefootExtensionTypesFor,
  forefootWedgeTypesFor,
} from './prescription/forefootRules';
import { shellThicknessOptionsFor } from './prescription/shellThicknessRules';
import {
  evaThicknessProblem,
  formatEvaThicknessMm,
} from './prescription/evaThicknessRules';

export interface UnavailableValue {
  /** Stable machine key, matching the MissingField convention. */
  key: string;
  /** Human-readable field name, e.g. "Rearfoot wedge type (L)". */
  label: string;
  /** The value sitting in state that this product does not offer. */
  value: string;
  /**
   * Human label for that value, e.g. "Rearfoot post (intrinsic)". Falls back
   * to the raw key when unknown: showing `rearfoot_post_intrinsic` is ugly but
   * unambiguous, and silently hiding an unrecognised value is the failure this
   * whole module exists to prevent.
   */
  valueLabel: string;
  /** Card anchor so the UI can expand the section and scroll to it. */
  cardId: string;
  /** Path to the field inside `form.deep`, for writing a replacement back. */
  path: readonly string[];
  /**
   * The single option the target offers, when it offers exactly one. The
   * caller may auto-select it: with one choice there is no clinical decision
   * being made. Absent when the target offers several (or none).
   */
  soleOption?: string;
  /**
   * A ready-made first-issue message, for a field whose rule is a numeric
   * range rather than a closed list (EVA thickness). The generic phrasing,
   * "is set to 7 mm, which is not available for this product. Choose one.",
   * would send the podiatrist looking for a dropdown; this is the same text
   * the input shows under itself. checkUnavailableValues prefers it.
   */
  message?: string;
}

/**
 * The unavailable values keyed by `key`, so a section can ask about one of its
 * own fields without re-deriving anything or scanning an array on every render.
 *
 * WHY THE SECTIONS GET THIS AT ALL, given SelectSm already paints an unknown
 * value red on its own: that local check is correct but CONDITIONAL on the call
 * site having narrowed its option list, and it has no idea what the value is
 * called. Two consequences, both live before this was threaded through:
 *
 *  * Shell thickness on a carbon or fibreglass insole is a PillRow, not a
 *    SelectSm. An unavailable value simply left every pill unselected, with
 *    nothing red anywhere. 57 live lines carry the retired carbon 2.0mm, and
 *    the banner named a field that looked perfectly fine on the form.
 *  * SelectSm's `unavailableLabel` was never passed by anybody, so where it did
 *    fire it showed the raw enum (`rearfoot_post_intrinsic`) while the banner
 *    three inches above showed "Rearfoot post (intrinsic)". Same field, two
 *    names, and the operator has to guess they are the same thing.
 *
 * Threading the checker's own result down means the banner and the field are
 * driven by ONE computation, so they cannot disagree about which field is at
 * fault or what the offending value is called.
 */
export type UnavailableLookup = Readonly<Record<string, UnavailableValue>>;

export function indexUnavailable(
  list: readonly UnavailableValue[],
): UnavailableLookup {
  const out: Record<string, UnavailableValue> = {};
  for (const u of list) out[u.key] = u;
  return out;
}

/** Presentational labels, kept next to the checks that emit them. */
const SIDE = { left: 'L', right: 'R' } as const;
type Side = keyof typeof SIDE;
const SIDES: readonly Side[] = ['left', 'right'];

/**
 * Display labels for the enum values this module can report.
 *
 * Duplicated from the option constants in the section components on purpose:
 * those live in .tsx files, and importing one would pull React (and through it
 * the eager supabase client) into a module that must stay unit-testable with
 * plain object fixtures. The list is small, closed, and only ever read for
 * copy, so drift here degrades a message rather than a rule.
 */
export const VALUE_LABELS: Readonly<Record<string, string>> = {
  // Rearfoot wedge types
  kirby_skive: 'Kirby skive',
  rearfoot_post_intrinsic: 'Rearfoot post (intrinsic)',
  rearfoot_eva_post_extrinsic: 'Rearfoot EVA post (extrinsic)',
  // Heel raise / forefoot types
  intrinsic: 'Intrinsic',
  extrinsic: 'Extrinsic',
  // Heel raise lengths
  short: 'Short (calcaneus area)',
  long: 'Long (to MTT area)',
  // Subcalcaneal offloading
  heel_offloading: 'Heel offloading',
  heel_cutout: 'Heel cutout',
  none: 'None',
};

function check(
  out: UnavailableValue[],
  args: {
    key: string;
    label: string;
    cardId: string;
    path: readonly string[];
    value: string | null | undefined;
    allowed: readonly string[] | null;
  },
): void {
  const { key, label, cardId, path, value, allowed } = args;
  // `null` allowed-list means the field is not enumerated for this product
  // (e.g. a free numeric shell thickness). Membership is not a meaningful
  // question, so say nothing.
  if (allowed === null) return;
  if (value == null || value === '') return;
  if (allowed.includes(value)) return;
  out.push({
    key,
    label,
    value,
    valueLabel: VALUE_LABELS[value] ?? value,
    cardId,
    path,
    // Auto-select only when the target offers exactly one option AND that
    // option is not "None". One option means no clinical judgement is being
    // made; "None" is different, because silently resolving to it DELETES a
    // modification the prescriber asked for, with nobody deciding to.
    ...(allowed.length === 1 && allowed[0] !== undefined && allowed[0] !== 'none'
      ? { soleOption: allowed[0] }
      : {}),
  });
}

/**
 * Values in `form` that `subtype` does not offer.
 *
 * SAFETY RULE, and it is load-bearing: never report a field that has no UI in
 * the current product. A blocked order whose offending field is not rendered
 * anywhere is unsubmittable with no remedy, which is strictly worse than the
 * bug being fixed. That is why the modification regions are skipped entirely
 * for refurbishment (PrescriptionForm renders none of them) and why the design
 * fields resolve to `null` for sandals.
 *
 * COVERAGE, stated honestly. Checked: rearfoot wedge type, heel-raise type and
 * length, subcalcaneal offloading type, forefoot wedge type, Morton extension
 * type, metatarsal extension type on non-sandals only, enumerated shell
 * thickness, and the EVA thickness range. NOT checked: the extension softness
 * and thickness cascades, whose
 * legal set depends on a sibling value that may itself be illegal. Resolving
 * that ordering is mapping policy and belongs to backlog item 5. Those two
 * fields still get the visible unavailable-value treatment at render; they
 * just do not block submit.
 */
export function collectUnavailableValues(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
): UnavailableValue[] {
  const out: UnavailableValue[] = [];
  const deep = form.deep;
  if (!deep) return out;

  const isSandal = subtype === 'orthopaedic_sandal';
  const isRefurb = subtype === 'insoles_refurbishment';

  // ---- Insole design ------------------------------------------------------
  // Sandals render SandalStyleSection instead, and refurbishment renders a
  // stripped design card. shellThicknessOptionsFor returns null for every
  // product that does not enumerate thickness, which covers both.
  const design = deep.design;
  if (design && !isSandal) {
    check(out, {
      key: 'design_shell_thickness',
      label: 'Shell thickness',
      cardId: CARD_IDS.topCover,
      path: ['design', 'shell_thickness'],
      value: design.shell_thickness ?? null,
      allowed: isRefurb
        ? null // refurbishment re-covers an existing shell; thickness is not asked
        : shellThicknessOptionsFor(subtype, design.fibreglass_color ?? null),
    });
    // EVA base thickness: a free number (2 to 6 mm, at most one decimal),
    // asked only on an EVA insole (the control exists nowhere else, per the
    // safety rule above) and OPTIONAL there, so absence is not reported. Not
    // a membership check: the rule is evaThicknessProblem, the same function
    // the input shows inline, so the gate and the field cannot disagree. No
    // sole option, ever: nothing here substitutes a value.
    if (subtype === 'eva_insole' && design.eva_thickness_mm != null) {
      const problem = evaThicknessProblem(design.eva_thickness_mm);
      if (problem) {
        out.push({
          key: 'design_eva_thickness',
          label: 'EVA thickness',
          value: String(design.eva_thickness_mm),
          valueLabel: `${formatEvaThicknessMm(design.eva_thickness_mm)} mm`,
          cardId: CARD_IDS.topCover,
          path: ['design', 'eva_thickness_mm'],
          message: problem,
        });
      }
    }
  }

  // ---- Modification regions ----------------------------------------------
  // PrescriptionForm renders NONE of these for refurbishment (the lab is
  // re-covering an existing shell), so a value here has no UI to fix it.
  if (isRefurb) return out;

  // Rearfoot wedge type.
  const rw = deep.rearfoot?.rearfoot_wedge;
  if (rw) {
    const allowed = rearfootWedgeTypesFor(subtype ?? null, isSandal);
    for (const side of SIDES) {
      // Normalise the pre-R34 enum first, exactly as the section and the
      // exporter do, so a legacy draft is not reported as illegal when it is
      // merely spelled the old way.
      const raw = side === 'left' ? rw.left_type : rw.right_type;
      const value = normalizeRearfootWedgeType(raw);
      check(out, {
        key: `rearfoot_wedge_${side}_type`,
        label: `Rearfoot wedge type (${SIDE[side]})`,
        cardId: CARD_IDS.rearfootWedge,
        path: ['rearfoot', 'rearfoot_wedge', `${side}_type`],
        value: value ?? null,
        allowed: allowed as readonly string[],
      });
    }
  }

  // Heel raise type, and its dependent length.
  const hr = deep.rearfoot?.heel_raise;
  if (hr) {
    const allowedTypes = heelRaiseTypesFor(subtype ?? null, isSandal);
    for (const side of SIDES) {
      const type = side === 'left' ? hr.left_type : hr.right_type;
      const before = out.length;
      check(out, {
        key: `heel_raise_${side}_type`,
        label: `Heel raise type (${SIDE[side]})`,
        cardId: CARD_IDS.heelRaise,
        path: ['rearfoot', 'heel_raise', `${side}_type`],
        value: type ?? null,
        allowed: allowedTypes as readonly string[],
      });
      // Only judge the length when the type it depends on is itself legal.
      // Otherwise the length is questionable purely because its parent is
      // wrong, and reporting both sends the podiatrist chasing a field that
      // will resolve itself once they fix the type.
      if (out.length === before) {
        const length = side === 'left' ? hr.left_length : hr.right_length;
        check(out, {
          key: `heel_raise_${side}_length`,
          label: `Heel raise length (${SIDE[side]})`,
          cardId: CARD_IDS.heelRaise,
          path: ['rearfoot', 'heel_raise', `${side}_length`],
          value: length ?? null,
          allowed: heelRaiseLengthsFor(type, isSandal) as readonly string[],
        });
      }
    }
  }

  // Subcalcaneal offloading type.
  const subcal = deep.rearfoot?.subcalcaneal_offloading;
  if (subcal) {
    const allowed = subcalOffloadingTypesFor(isSandal);
    for (const side of SIDES) {
      check(out, {
        key: `subcalcaneal_offloading_${side}_type`,
        label: `Subcalcaneal offloading type (${SIDE[side]})`,
        cardId: CARD_IDS.subcalcanealOffloading,
        path: ['rearfoot', 'subcalcaneal_offloading', side, 'type'],
        value: subcal[side]?.type ?? null,
        allowed: allowed as readonly string[],
      });
    }
  }

  // Forefoot wedge type.
  const fw = deep.forefoot?.forefoot_wedge;
  if (fw) {
    const allowed = forefootWedgeTypesFor(isSandal);
    for (const side of SIDES) {
      check(out, {
        key: `forefoot_wedge_${side}_type`,
        label: `Forefoot wedge type (${SIDE[side]})`,
        cardId: CARD_IDS.forefootWedge,
        path: ['forefoot', 'forefoot_wedge', `${side}_type`],
        value: (side === 'left' ? fw.left_type : fw.right_type) ?? null,
        allowed: allowed as readonly string[],
      });
    }
  }

  // Extension types.
  //
  // These two cards look interchangeable and are NOT, which is why they are
  // handled separately and commented:
  //
  //  * Morton extension IS rendered on sandals (ForefootSection renders it
  //    outside the !isSandal block and passes isSandal down so it narrows its
  //    own options), and prescriptionExcelExport writes it unconditionally.
  //    So an illegal value here reaches the lab sheet, and it is checkable
  //    because there is a control to fix it on.
  //
  //  * The 2nd-5th metatarsal extension is NOT rendered on sandals at all
  //    (ForefootSection Row C is wrapped in {!isSandal ? ... : null}) and the
  //    exporter skips it the same way. Checking it would block submit on a
  //    field with no control and no lab output, which is the safety rule
  //    above, and there is no remedy short of discarding the whole copy.
  //    5 live lines carry an extrinsic value that would have done exactly
  //    that on the PR's own headline sandal-reuse flow.
  const morton = deep.forefoot?.morton_extension;
  if (morton) {
    const allowed = forefootExtensionTypesFor(isSandal);
    for (const side of SIDES) {
      check(out, {
        key: `morton_extension_${side}_type`,
        label: `Morton extension type (${SIDE[side]})`,
        cardId: CARD_IDS.mortonExtension,
        path: ['forefoot', 'morton_extension', `${side}_type`],
        value: (side === 'left' ? morton.left_type : morton.right_type) ?? null,
        allowed: allowed as readonly string[],
      });
    }
  }

  // metatarsal_extension: deliberately NOT checked on sandals, per above. On
  // non-sandals it is rendered, but its option set does not narrow by shell,
  // so there is nothing for this product axis to catch.
  const me = deep.forefoot?.metatarsal_extension;
  if (me && !isSandal) {
    const allowed = forefootExtensionTypesFor(false);
    for (const side of SIDES) {
      check(out, {
        key: `metatarsal_extension_${side}_type`,
        label: `Metatarsal extension type (${SIDE[side]})`,
        cardId: CARD_IDS.metatarsalExtension,
        path: ['forefoot', 'metatarsal_extension', `${side}_type`],
        value: (side === 'left' ? me.left_type : me.right_type) ?? null,
        allowed: allowed as readonly string[],
      });
    }
  }

  return out;
}

/** Immutably write `value` at `path` inside a deep-state object. */
function setAtPath(
  root: Record<string, unknown>,
  path: readonly string[],
  value: string,
): Record<string, unknown> {
  const [head, ...rest] = path;
  if (head === undefined) return root;
  if (rest.length === 0) return { ...root, [head]: value };
  const child = root[head];
  const childObj =
    child && typeof child === 'object' && !Array.isArray(child)
      ? (child as Record<string, unknown>)
      : {};
  return { ...root, [head]: setAtPath(childObj, rest, value) };
}

/**
 * Resolve the unavailable values that have exactly one legal replacement.
 *
 * Rafael's rule, and it is a good one: where the target offers a single option
 * there is no clinical judgement being made, so making the podiatrist click it
 * is friction with no safety benefit. Where the target offers several, the
 * field is deliberately LEFT for them to decide (see the module header).
 *
 * Call this at the point a prescription is copied in, never during render and
 * never from an effect. Writing form state while rendering is an anti-pattern
 * that can loop, and an effect would re-fire and fight a podiatrist who had
 * just cleared the field on purpose.
 *
 * Returns the (possibly unchanged) form plus the list of what it resolved, so
 * the caller can tell the operator rather than changing the prescription
 * behind their back.
 */
export function applySoleOptions(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
): { form: PrescriptionFormState; applied: UnavailableValue[] } {
  const applied = collectUnavailableValues(form, subtype).filter((u) => u.soleOption);
  if (applied.length === 0) return { form, applied: [] };
  let deep = (form.deep ?? {}) as Record<string, unknown>;
  for (const u of applied) {
    if (u.soleOption === undefined) continue;
    deep = setAtPath(deep, u.path, u.soleOption);
  }
  // `deep` is necessarily a real object on this branch: `applied` is non-empty,
  // and collectUnavailableValues returns nothing when `form.deep` is absent.
  return {
    form: { ...form, deep: deep as NonNullable<PrescriptionFormState['deep']> },
    applied,
  };
}

/**
 * First-issue message for the wizard's Next-button reason text, matching the
 * `check*` single-message convention in prescriptionValidation.
 *
 * The message NAMES THE SOURCE ORDER when one is known. "Rearfoot wedge type
 * is not available" invites the reader to assume a bug; "carried from order
 * 2600774, not available on an orthopaedic sandal" tells them what happened and
 * that the fix is one dropdown.
 */
export function checkUnavailableValues(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
  sourceOrderNumber?: string | null | undefined,
): string | null {
  const first = collectUnavailableValues(form, subtype)[0];
  if (!first) return null;
  // A numeric-range field carries its own instruction; the "not available,
  // choose one" phrasing below is for closed lists.
  if (first.message) return first.message;
  const from = sourceOrderNumber ? ` carried from order ${sourceOrderNumber},` : '';
  return `${first.label} is set to "${first.valueLabel}",${from} which is not available for this product. Choose one.`;
}
